import React, { useEffect, useMemo, useState } from "react";
import { addDoc, collection, deleteDoc, doc, getDocs, updateDoc } from "firebase/firestore";
import { db } from "../firebase/firebase-config";

const BankAccountSubmitted = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [message, setMessage] = useState("");
  const [actionId, setActionId] = useState("");

  useEffect(() => {
    const fetchRecords = async () => {
      try {
        const snap = await getDocs(collection(db, "bank-account-submissions"));
        const list = snap.docs.map((item) => ({ id: item.id, ...item.data() }));
        setRecords(list);
      } catch (error) {
        console.error("Unable to fetch bank account submissions:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchRecords();
  }, []);

  const filteredRecords = useMemo(() => {
    const term = search.trim().toLowerCase();
    return records
      .filter((item) => item.status === "submitted")
      .filter((item) => {
        if (!term) return true;
        return [item.name, item.accountHolderName, item.mobileNumber, item.bankType, item.accountDetails, item.ifscCode]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(term));
      })
      .sort((a, b) => (b.createDate || "").localeCompare(a.createDate || ""));
  }, [records, search]);

  const handleAccept = async (record) => {
    setActionId(record.id);
    setMessage("");

    try {
      await addDoc(collection(db, "bank-account-details"), {
        name: record.name || "",
        accountHolderName: record.accountHolderName || "",
        mobileNumber: record.mobileNumber || "",
        bankType: record.bankType || "",
        accountDetails: record.accountDetails || "",
        ifscCode: record.ifscCode || "",
        verifyStatus: "not_verified",
        createDate: new Date().toISOString(),
        submittedFrom: "bank-account-submissions",
      });

      await updateDoc(doc(db, "bank-account-submissions", record.id), {
        status: "accepted",
        statusUpdatedAt: new Date().toISOString(),
      });

      setRecords((prev) =>
        prev.map((item) =>
          item.id === record.id
            ? { ...item, status: "accepted", statusUpdatedAt: new Date().toISOString() }
            : item
        )
      );
      setMessage("Bank account submission accepted and moved to bank account list.");
    } catch (error) {
      console.error("Unable to accept bank account submission:", error);
      setMessage("Unable to accept submission right now.");
    } finally {
      setActionId("");
    }
  };

  const handleDelete = async (recordId) => {
    setActionId(recordId);
    setMessage("");

    try {
      await deleteDoc(doc(db, "bank-account-submissions", recordId));
      setRecords((prev) => prev.filter((item) => item.id !== recordId));
      setMessage("Submission deleted successfully.");
    } catch (error) {
      console.error("Unable to delete bank account submission:", error);
      setMessage("Unable to delete submission right now.");
    } finally {
      setActionId("");
    }
  };

  return (
    <div className="container-fluid py-4">
      <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3 mb-4">
        <div>
          <h2 className="fw-bold mb-1">Bank Account Submitted</h2>
          <p className="text-muted mb-0">Public bank account entries appear here before moving to the main list.</p>
        </div>
        <input
          type="text"
          className="form-control"
          placeholder="Search by name, mobile, bank, account or IFSC"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          style={{ maxWidth: 360, borderRadius: 14 }}
        />
      </div>

      {message && (
        <div className={`alert ${message.includes("Unable") ? "alert-danger" : "alert-success"} rounded-4`}>
          {message}
        </div>
      )}

      <div className="table-responsive">
        <table className="table table-bordered table-hover align-middle">
          <thead className="table-light">
            <tr>
              <th>#</th>
              <th>Name</th>
              <th>Account Holder</th>
              <th>Mobile</th>
              <th>Bank</th>
              <th>Account Number</th>
              <th>IFSC Code</th>
              <th>Submitted At</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="9" className="text-center py-4">
                  Loading submissions...
                </td>
              </tr>
            ) : filteredRecords.length ? (
              filteredRecords.map((item, index) => (
                <tr key={item.id}>
                  <td>{index + 1}</td>
                  <td>{item.name}</td>
                  <td>{item.accountHolderName || "-"}</td>
                  <td>{item.mobileNumber}</td>
                  <td>{item.bankType}</td>
                  <td>{item.accountDetails}</td>
                  <td>{item.ifscCode}</td>
                  <td>{item.createDate ? new Date(item.createDate).toLocaleString("en-IN") : "-"}</td>
                  <td>
                    <div className="d-flex gap-2">
                      <button
                        type="button"
                        className="btn btn-success btn-sm"
                        disabled={actionId === item.id}
                        onClick={() => handleAccept(item)}
                      >
                        Accept
                      </button>
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        disabled={actionId === item.id}
                        onClick={() => handleDelete(item.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="9" className="text-center py-4">
                  No submitted bank accounts found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BankAccountSubmitted;
